import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, queryString } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import type { SlotSuggestion } from '../../../shared/types/index.ts';
import { weekdayLabel } from './format.ts';
import { dayOfWeekOf } from '../../../shared/calculations/recurrence.ts';
import { Button } from '../ui.tsx';
import { formatLocalTime, formatNumericDate } from '../../i18n/datetime.ts';
export function TeamSuggestions({
  from,
  enabled = true,
  durationMinutes,
  frequency,
  onPick,
}: {
  enabled?: boolean;
  from: string;
  durationMinutes: number;
  frequency: string;
  onPick: (teamId: string, memberIds: string[], pick: SlotSuggestion) => void;
}) {
  const { t } = useTranslation(),
    { company, currentLocation } = useSession();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: [
      'availability-team-suggestions',
      company?.id,
      currentLocation?.id,
      from,
      durationMinutes,
      frequency,
      page,
    ],
    queryFn: () =>
      api.getFresh<{
        items: Array<{
          teamId: string;
          name: string;
          memberIds: string[];
          suggestion: SlotSuggestion;
          horizonDays: number;
        }>;
        uncertain: boolean;
        nextPage: number | null;
      }>(
        `/schedule/availability/team-suggestions${queryString({ from, durationMinutes, frequency, page, pageSize: 10 })}`,
      ),
    enabled: Boolean(currentLocation) && durationMinutes >= 15,
    retry: false,
  });
  return (
    <section className="my-3" aria-label={t('planning.recommendTeams')}>
      <h3 className="field-label">{t('planning.recommendTeams')}</h3>
      <p className="field-hint">{t('planning.recommendHint')}</p>
      {query.isPending ? (
        <p>{t('suggestions.loading')}</p>
      ) : query.isError ? (
        <Button onClick={() => void query.refetch()}>{t('planning.retry')}</Button>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {query.data.items.map((item) => (
              <Button
                key={item.teamId}
                disabled={!enabled}
                onClick={() => onPick(item.teamId, item.memberIds, item.suggestion)}
              >
                {t('planning.recommendPick', {
                  name: item.name,
                  date: formatNumericDate(item.suggestion.startsOn),
                  time: formatLocalTime(item.suggestion.startTimeLocal),
                })}{' '}
                · {item.suggestion.occurrencesChecked - item.suggestion.occurrencesClashing}/
                {item.suggestion.occurrencesChecked}
                {frequency === 'monthly' && item.suggestion.nthWeek != null && (
                  <span className="block text-xs">
                    {t('planning.monthlyPattern', {
                      week: item.suggestion.nthWeek,
                      day: weekdayLabel(dayOfWeekOf(item.suggestion.startsOn)),
                    })}
                  </span>
                )}
              </Button>
            ))}
          </div>
          {query.data.items.length === 0 && <p>{t('planning.recommendNone')}</p>}
          {query.data.uncertain && <p role="status">{t('planning.recommendUncertain')}</p>}
          <div className="flex gap-2">
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
              {t('planning.previous')}
            </Button>
            <span>{page}</span>
            <Button disabled={!query.data.nextPage} onClick={() => setPage(query.data.nextPage!)}>
              {t('planning.next')}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
